import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Course, AppSettings, GPACalculationResult, SchoolContext } from '@gpa-scales/shared-types';
import { GradeLevel, AGSubject } from '@gpa-scales/shared-types';
import { formatGPA } from '../utils/format';

const GRADE_LEVEL_LABELS: Record<number, string> = {
  [GradeLevel.Freshman]: '9th',
  [GradeLevel.Sophomore]: '10th',
  [GradeLevel.Junior]: '11th',
  [GradeLevel.Senior]: '12th',
};

export function generatePDFReport(
  courses: Course[],
  _settings: AppSettings,
  result: GPACalculationResult,
  context?: SchoolContext
): jsPDF {
  const doc = new jsPDF();

  // Title
  doc.setFontSize(18);
  doc.text('GPA Scales — GPA Report', 14, 22);

  if (context?.schoolName) {
    doc.setFontSize(10);
    doc.text(`School: ${context.schoolName}`, 14, 28);
  }

  doc.setFontSize(8);
  doc.setTextColor(128);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, context?.schoolName ? 32 : 28);
  doc.setTextColor(0);

  // GPA Summary
  doc.setFontSize(14);
  doc.text('GPA Summary', 14, 38);

  autoTable(doc, {
    startY: 40,
    head: [['Variant', 'GPA', 'Courses', 'Credits']],
    body: result.variants.map((v) => [
      v.label,
      formatGPA(v.value),
      `${v.includedCourses}/${v.totalCourses}`,
      v.totalCredits.toFixed(1),
    ]),
    theme: 'striped',
    headStyles: { fillColor: [59, 130, 246] },
  });

  // Honors Cap
  const honorsY = (doc as any).lastAutoTable?.finalY ?? 80;
  doc.setFontSize(14);
  doc.text(`Honors Cap: ${result.honorsCap.usedPoints}/${result.honorsCap.totalCap}`, 14, honorsY + 10);

  // Course Table
  doc.setFontSize(14);
  doc.text('Courses', 14, honorsY + 18);

  autoTable(doc, {
    startY: honorsY + 20,
    head: [['Name', 'Grade', 'Level', 'Type', 'A-G', 'Credits']],
    body: courses.map((c) => [
      c.name,
      c.grade,
      GRADE_LEVEL_LABELS[c.gradeLevel] ?? String(c.gradeLevel),
      c.courseType.toUpperCase(),
      c.agSubject === AGSubject.None ? '—' : c.agSubject,
      c.credits.toFixed(1),
    ]),
    theme: 'striped',
    headStyles: { fillColor: [59, 130, 246] },
  });

  // Validation Issues
  if (result.validationIssues.length > 0) {
    doc.addPage();
    doc.setFontSize(14);
    doc.text('Validation Issues', 14, 22);

    autoTable(doc, {
      startY: 26,
      head: [['Severity', 'Message', 'Suggestion']],
      body: result.validationIssues.map((v) => [
        v.severity.toUpperCase(),
        v.message,
        v.suggestion ?? '',
      ]),
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246] },
    });
  }

  // Methodology
  const methodsY = (doc as any).lastAutoTable?.finalY ?? 100;
  if (methodsY > 250) {
    doc.addPage();
  }
  const finalY = methodsY > 250 ? 22 : methodsY + 10;
  doc.setFontSize(14);
  doc.text('Methodology', 14, finalY);
  doc.setFontSize(8);
  doc.text(
    'UC Capped GPA: A-G courses in 10th-11th, max 8 honors points (max 4 in 10th grade). ' +
    'UC Uncapped: A-G, 10th-11th, no honors cap. ' +
    'Unweighted: All courses, no bonus points. ' +
    'Common App Weighted: All courses, all years, bonus for advanced coursework.',
    14,
    finalY + 6,
    { maxWidth: 180 }
  );

  return doc;
}
